import { useState } from 'react'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  CryptoIcon,
} from '../ui'
import { Check, Flag, FlagOff, X } from 'lucide-react'
import { type SwapData, SwapStatus } from './types'
import { ellipsise } from '../../utils/strings'
import { formatDate, getNetworkName } from '../../lib/transaction-utils'
import { ExternalApiBadge, FlaggedBadge, StatusBadge } from '../badges/status'
import { SkeletonTable } from '../skeleton/Skeleton'

export interface SwapsTableProps {
  swaps: SwapData[]
  loading?: boolean
  showApproveButton?: boolean
  showRejectButton?: boolean
  showFlagButton?: boolean
  isComplianceUser?: boolean

  // Callbacks
  onApproveSwap?: (swapId: string) => Promise<void>
  onRejectSwap?: (swapId: string, reason: string) => Promise<void>
  onViewSwapDetails?: (swapId: string) => void

  // Modal trigger handlers (exposed to parent)
  onModalHandlersReady?: (handlers: { handleExportClick: () => void }) => void
}

export function SwapsTable({
  swaps,
  loading = false,
  showApproveButton = false,
  showRejectButton = false,
  showFlagButton = false,
  isComplianceUser = false,
  onApproveSwap,
  onRejectSwap,
  onViewSwapDetails,
}: SwapsTableProps) {
  // Modal states
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [selectedSwapId, setSelectedSwapId] = useState<string | null>(null)

  // Loading states
  const [isRejecting, setIsRejecting] = useState(false)

  // Handler functions

  const showRejectDialog = (swapId: string) => {
    setSelectedSwapId(swapId)
    setRejectDialogOpen(true)
  }

  const handleRejectConfirm = async () => {
    if (!selectedSwapId || !onRejectSwap) return

    setIsRejecting(true)
    try {
      await onRejectSwap(selectedSwapId, 'Rejected by admin')
      setRejectDialogOpen(false)
      setSelectedSwapId(null)
    } catch (_error) {
      // Error handling done by parent
    } finally {
      setIsRejecting(false)
    }
  }

  const handleDialogCancel = () => {
    setRejectDialogOpen(false)
    setSelectedSwapId(null)
  }

  if (loading) {
    return <SkeletonTable />
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md">
        <Table>
          <TableHeader>
            <TableRow className="h-[60px]">
              {showFlagButton && <TableHead className="w-[50px]">Flag</TableHead>}
              <TableHead className="w-[100px]">ID</TableHead>
              <TableHead>Group</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Requested By</TableHead>
              <TableHead>Decision By</TableHead>
              <TableHead>Amounts</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="w-[100px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {swaps.length === 0 ? (
              <TableRow className="h-[60px]">
                <TableCell
                  colSpan={showFlagButton ? 10 : 9}
                  className="text-center text-muted-foreground py-8"
                >
                  No swaps found
                </TableCell>
              </TableRow>
            ) : (
              swaps.map((swap, index) => (
                <TableRow key={`${swap.id}-${index}`} className="h-[60px]">
                  {showFlagButton && (
                    <TableCell>
                      <div className="h-8 w-8 p-0 flex items-center justify-center">
                        {swap.isFlagged ? (
                          <Flag className="h-4 w-4 text-red-500 fill-red-500" />
                        ) : (
                          <FlagOff className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </TableCell>
                  )}
                  <TableCell className="font-medium">{ellipsise(swap.id, 20)}</TableCell>
                  <TableCell className="font-medium">{swap.group}</TableCell>
                  <TableCell className="font-medium">{swap.company}</TableCell>
                  <TableCell>
                    {swap.requestedBy || <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell>
                    {swap.decisionBy || <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <>
                        <span>
                          <CryptoIcon
                            symbol={swap.fromMint.symbol || ''}
                            imageOverride={swap.fromMint.image || ''}
                          />
                        </span>

                        <span className="text-gray-500">{swap.fromAmount}</span>
                        <div className="flex flex-col justify-center">
                          <span className="text-xs leading-none">{swap.fromMint.symbol}</span>
                          <span className="text-xs text-gray-400 leading-none">
                            {getNetworkName(swap.fromMint.chain || '', swap.fromMint.symbol || '')}
                          </span>
                        </div>
                        <span className="text-gray-400">→</span>

                        <span>
                          <CryptoIcon
                            symbol={swap.toMint.symbol || ''}
                            imageOverride={swap.toMint.image || ''}
                          />
                        </span>
                        <span className="text-gray-500">{swap.toAmount}</span>
                        <div className="flex flex-col justify-center">
                          <span className="text-xs leading-none">{swap.toMint.symbol}</span>
                          <span className="text-xs leading-none">
                            {getNetworkName(swap.toMint.chain || '', swap.toMint.symbol || '')}
                          </span>
                        </div>
                      </>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={swap.status} type="TRADE" />
                      <FlaggedBadge flagged={swap.isFlagged} />
                      <ExternalApiBadge isExternalApi={swap.isExternalApi} />
                    </div>
                  </TableCell>
                  <TableCell>
                    {formatDate(swap.completedAt || swap.decisionAt || swap.createdAt || '')}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {swap.status === SwapStatus.Requested &&
                        !isComplianceUser &&
                        showApproveButton &&
                        showRejectButton && (
                          <>
                            <Button
                              variant="main_action"
                              size="sm"
                              className=""
                              onClick={async () => await onApproveSwap?.(swap.id)}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="main_outline"
                              size="sm"
                              className="hover:bg-main/5"
                              onClick={() => showRejectDialog(swap.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      {onViewSwapDetails && (
                        <Button
                          variant="main_outline"
                          size="sm"
                          onClick={() => onViewSwapDetails(swap.id)}
                        >
                          View details
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Reject Confirmation Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Swap</DialogTitle>
            <DialogDescription>
              Are you sure you want to reject this swap? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleDialogCancel}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRejectConfirm} disabled={isRejecting}>
              {isRejecting ? 'Rejecting...' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
